import { matchOnExpression } from '.';

import type {
  AttributeCondition,
  AuthStrategies,
  Course,
  User
} from 'shared-types';

interface Params {
  conditions: AttributeCondition[];
  customAttributeValues: User['customAttributes'] | Course['customAttributes'];
  isRequiredCheck?: boolean;
  omitCustomAttributes?: boolean;
  user?: {
    authenticatorType?: AuthStrategies;
    email?: User['email'];
    isSelfRegistered?: User['isSelfRegistered'];
  };
}

const attributesConditionsMatch = ({
  conditions,
  customAttributeValues,
  isRequiredCheck = false,
  omitCustomAttributes,
  user
}: Params) => {
  if (!conditions || !conditions.length) return true;

  // each condition has a key and a value
  // currently each key is required to match value
  return conditions.every((condition) => {
    if (condition.key === 'email') {
      const email = user?.email;
      return matchOnExpression({
        expression: condition.expression,
        userValue: email || '',
        reference: condition.value as string
      });
    }

    if (condition.key === 'emailDomain') {
      const emailDomain = user?.email?.slice(user?.email?.lastIndexOf('@') + 1);
      return matchOnExpression({
        expression: condition.expression,
        userValue: emailDomain || '',
        reference: condition.value as string
      });
    }

    if (condition.key === 'authenticationType') {
      const authenticatorType = user?.authenticatorType;

      // if user does not have an authenticatorType, we assume that an admin
      // is looking at another users data, and then we do not want to limit
      // based on authenticator type. so we return the result based only
      // on whether this is a required attribute or not
      if (!authenticatorType) {
        if (isRequiredCheck) {
          // don't consider attribute as required
          return false;
        } else {
          // do consider attribute as applicable
          return true;
        }
      }

      const userAuthenticatorTypeValue =
        authenticatorType !== 'guest' &&
        authenticatorType !== 'local' &&
        authenticatorType !== 'local-totp'
          ? 'SSO'
          : 'local';

      return matchOnExpression({
        expression: condition.expression,
        userValue: userAuthenticatorTypeValue,
        reference: condition.value as string
      });
    }

    if (condition.key === 'isSelfRegistered') {
      return matchOnExpression({
        expression: condition.expression,
        userValue: user?.isSelfRegistered || false,
        reference: condition.value
      });
    }

    if (omitCustomAttributes) return true;

    return matchOnExpression({
      expression: condition.expression,
      userValue: customAttributeValues?.[condition.key] || '',
      reference: condition.value
    });
  });
};

export default attributesConditionsMatch;
